import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid,
  Tooltip, Legend, ResponsiveContainer
} from "recharts";

function SummaryStatsChart({ entity, metric, period = "1Y" }) {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const getStartDateFromPeriod = (period) => {
    const now = new Date();
    const date = new Date(now);
    if (period.endsWith("Q")) {
      date.setMonth(date.getMonth() - parseInt(period[0]) * 3);
    } else if (period.endsWith("Y")) {
      date.setFullYear(date.getFullYear() - parseInt(period[0]));
    }
    return date.toISOString().split("T")[0];
  };

  const startDate = getStartDateFromPeriod(period);
  const endDate = new Date().toISOString().split("T")[0];

  useEffect(() => {
    if (!metric || typeof metric !== "string") return;

    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await axios.get(
          `${process.env.REACT_APP_API_BASE_URL}/api/financials/${entity}`,
          {
            params: {
              metrics: metric,
              startDate,
              endDate
            }
          }
        );
        setData(res.data);
      } catch (err) {
        setError("Unable to fetch data.");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [entity, metric, period]);

  const transformedData = data
    .sort((a, b) => new Date(a.FiledDate) - new Date(b.FiledDate))
    .map(row => ({
      FiledDate: row.FiledDate,
      Value: isNaN(row.Value) ? null : Number(row.Value)
    }));

  const formatYAxis = (val) => {
    if (Math.abs(val) >= 1e9) return `${(val / 1e9).toFixed(1)}B`;
    if (Math.abs(val) >= 1e6) return `${(val / 1e6).toFixed(1)}M`;
    return val;
  };

  return (
    <div className="bg-white rounded p-4 shadow-md">
      <h2 className="text-lg font-semibold mb-2 text-gray-700">
        {metric ? `${metric} for ${entity}` : `Metric for ${entity}`}
      </h2>

      {loading ? (
        <p>Loading chart...</p>
      ) : error ? (
        <p className="text-red-500">{error}</p>
      ) : transformedData.length === 0 ? (
        <p className="text-gray-500">No data available for this metric.</p>
      ) : (
        <ResponsiveContainer width="100%" height={400}>
          <LineChart data={transformedData}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis
              dataKey="FiledDate"
              tickFormatter={(d) => new Date(d).toLocaleDateString()}
            />
            <YAxis tickFormatter={formatYAxis} />
            <Tooltip formatter={formatYAxis} />
            <Legend />
            <Line
              type="monotone"
              dataKey="Value"
              name={metric}
              stroke="#8884d8"
              dot={false}
            />
          </LineChart>
        </ResponsiveContainer>
      )}
    </div>
  );
}

export default SummaryStatsChart;
